/**
 * staged-data — access to files the user gave the chat: STAGED data files
 * (paperclip uploads routed to data staging via acceptExtensions, gated by
 * features.dataStaging.exposeToParent) and NORMAL chat attachments (paperclip
 * uploads that stay multimodal — the model can still see them — gated by
 * features.attachments.exposeToParent, usable-chat v1.171.0, fragment
 * 65d80036). Installed once per tool call from index.tsx (the capabilities
 * seam), consumed by image/logo sources so "use the logo I uploaded" works
 * without any CORS fetch or staging diversion.
 */
import { readActiveFolderFileAsDataUrl } from "@usable/office-embed";
import type { AttachmentsApi, StagedDataApi, WorkspaceFilesApi } from "@usable/office-embed";

let stagedApi: StagedDataApi | null = null;
let attachmentsApi: AttachmentsApi | null = null;
let workspaceFilesApi: WorkspaceFilesApi | null = null;

export function installStagedData(stagedData: StagedDataApi | undefined): void {
  if (stagedData) stagedApi = stagedData;
}

export function installAttachments(attachments: AttachmentsApi | undefined): void {
  if (attachments) attachmentsApi = attachments;
}

export function installWorkspaceFiles(workspaceFiles: WorkspaceFilesApi | undefined): void {
  if (workspaceFiles) workspaceFilesApi = workspaceFiles;
}

/** The installed workspace-files RPC (read + durable put), or null pre-install. */
export function getWorkspaceFilesApi(): WorkspaceFilesApi | null {
  return workspaceFilesApi;
}

export interface ChatFileInfo {
  /** Pass this (or fileName) to useUploadedImage / branding.logoUpload. */
  ref: string;
  fileName: string;
  mimeType?: string;
  sizeBytes?: number;
  source: "attachment" | "staged";
}

/**
 * Merged view of everything uploaded to this chat the parent can read, plus
 * NOTICES for anything that failed. A gate-off rejection must be LOUD —
 * silently returning [] made the model tell the user "no files uploaded" and
 * ask for re-uploads in a loop (found live).
 */
export async function listChatFiles(): Promise<{ files: ChatFileInfo[]; notices: string[] }> {
  const files: ChatFileInfo[] = [];
  const notices: string[] = [];

  if (attachmentsApi) {
    try {
      const result = await attachmentsApi.list();
      files.push(
        ...result.files.map((f) => ({
          ref: f.fileId,
          fileName: f.fileName,
          mimeType: f.mimeType,
          sizeBytes: f.sizeBytes,
          source: "attachment" as const,
        }))
      );
    } catch (err) {
      notices.push(
        `Chat attachments are NOT readable: ${err instanceof Error ? err.message : String(err)}. ` +
          `The embed config likely needs features.attachments.exposeToParent: true — tell the user.`
      );
    }
  }
  if (stagedApi) {
    try {
      const result = await stagedApi.list();
      files.push(
        ...result.files.map((f) => ({
          ref: f.alias,
          fileName: f.fileName,
          mimeType: f.mimeType,
          sizeBytes: f.sizeBytes,
          source: "staged" as const,
        }))
      );
    } catch (err) {
      // Data staging being off is common and fine when attachments work —
      // only surface it when attachments ALSO produced nothing.
      notices.push(
        `Staged data files are not readable: ${err instanceof Error ? err.message : String(err)}.`
      );
    }
  }

  return { files, notices };
}

export interface ChatFileResolution {
  dataUrl: string;
  /** Which pipe served the bytes — drives image-provenance durability. */
  source: "workspace" | "attachment" | "staged" | "folder";
  /** The address that worked (fileId / alias / folder path). */
  ref: string;
}

/**
 * Resolve an uploaded file by fileId/alias or (case-insensitive) file name to
 * a data URL + its source info. Folder first, then attachments (the common
 * paperclip path), then staged files, then workspace fileIds. Throws with the
 * available file list when nothing matches.
 */
export async function readChatFileWithInfo(refOrName: string): Promise<ChatFileResolution> {
  // The attached LOCAL FOLDER is a valid image source too (e.g. Brand/logo.png).
  const folderDataUrl = await readActiveFolderFileAsDataUrl(refOrName).catch((err) => {
    throw err; // matched a folder file but it's unusable — surface the real reason
  });
  if (folderDataUrl) return { dataUrl: folderDataUrl, source: "folder", ref: refOrName };

  if (!attachmentsApi && !stagedApi) {
    throw new Error(
      "Uploaded-file access is not available (neither attachments nor data staging is exposed by the embed config)."
    );
  }
  const { files, notices } = await listChatFiles();
  const wanted = refOrName.toLowerCase();
  const match =
    files.find((f) => f.ref === refOrName) ??
    files.find((f) => f.fileName?.toLowerCase() === wanted) ??
    files.find((f) => f.fileName?.toLowerCase().endsWith(wanted));
  if (!match) {
    // Last resort: treat the ref as a Usable WORKSPACE fileId (from the chat's
    // own search-files) — the v1.172.0 bridge reads it if the gate is on.
    if (workspaceFilesApi && !refOrName.includes(" ")) {
      try {
        const wf = await workspaceFilesApi.readBytes(refOrName);
        const mimeType = wf.mimeType || "image/png";
        return {
          dataUrl: `data:${mimeType};base64,${wf.base64.replace(/\s+/g, "")}`,
          source: "workspace",
          ref: refOrName,
        };
      } catch (err) {
        notices.push(
          `Workspace-file read for "${refOrName}" failed: ${err instanceof Error ? err.message : String(err)}.`
        );
      }
    }
    const available =
      files.map((f) => `${f.fileName} (${f.source}, ref ${f.ref})`).join(", ") || "none";
    const noticeSuffix = notices.length > 0 ? ` Notices: ${notices.join(" ")}` : "";
    throw new Error(
      `No uploaded file matches "${refOrName}" (checked the attached local folder, chat attachments, staged files, ` +
        `and workspace files). Files in this chat: ${available}.` +
        noticeSuffix +
        ` For a Usable WORKSPACE file, pass its fileId (from your search-files result). Otherwise use ` +
        `list_uploaded_files / local_folder_list to see what exists, or ask the user to attach the file.`
    );
  }

  const bytes =
    match.source === "attachment"
      ? await attachmentsApi!.readBytes(match.ref)
      : await stagedApi!.readBytes(match.ref);
  const mimeType = bytes.mimeType || "image/png";
  return {
    dataUrl: `data:${mimeType};base64,${bytes.base64.replace(/\s+/g, "")}`,
    source: match.source,
    ref: match.ref,
  };
}

/** Back-compat wrapper — callers that only need the bytes. */
export async function readStagedFileAsDataUrl(refOrName: string): Promise<string> {
  return (await readChatFileWithInfo(refOrName)).dataUrl;
}
